import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  growthFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import {
  Claimant,
  JointWaitingPeriod,
  partnerWaitingPeriodSeed,
  seedJointWaitingPeriod,
} from '../jointClaimant';
import { applyIndemnitySurplus } from '../sharedSurplus';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';

// ---------------------------------------------------------------------------
// Generic two-pool hybrid life insurance — the minimal common model for the
// `hybridLifeInsurance` calc-type products. One strategy serves four
// single-life types plus one joint:
//   - `allstateHybridPolicy`  (Allstate Whole Life with LTC)
//   - `secureCareThree`       (Securian SecureCare III)
//   - `secureCareFour`        (Securian SecureCare IV)
//   - `agisGroupPolicy`       (Trustmark Life + Care)
//   - `jointHybridAssetBased` (joint — shared pools, per-spouse EP; see below)
//
// JOINT (`jointHybridAssetBased`): the two pools and the death benefit are
// SHARED — both spouses accelerate the same single face — and each serves their
// own elimination period (mirrors joint Asset Care). Detected via
// `jointAnchorPolicyYear`; per-spouse EP lives in `strategyState.jointWaiting-
// Period`, while the pools pass through the universal slots unchanged.
//
// These replace the legacy generic hybrid path
// (`calcFundingSourceDerivatives.ts`, hybrid branch ~L2453-2482, daily-cap
// payout ~L845-882, indemnity ~L1542-1561). For Securian SCIII the repo
// carries an illustration + field-mapping (`docs/securian-securecare/`);
// Allstate and Trustmark remain unverified, and the defs carry
// `hasUnverifiedModeling: true`. Correctness is checked by this strategy's
// tests plus the runtime identity checks (the legacy-parity harness was
// retired 2026-06-11, P2-40) — this strategy mirrors legacy's minimal
// two-pool DB+COB mechanics and OMITS every product-specific rule the sibling
// strategies bake in (no Asset Care AOB/COB split, no CM II per-year rider
// escalation, no life-with-rider eob≡0 collapse).
//
// TWO POOLS — death-benefit (base) drawn first, then continuation (COB):
// - Base / death-benefit pool = `policyMaximumBenefitAmount` (the accelerable
//   face). COB / extension pool = `policyContinuationBenefitAmount`. LTC draws
//   deplete the base pool first; once exhausted the draw falls to the COB pool.
// - Only base-pool drawdown accelerates (reduces) the death benefit dollar-for-
//   dollar — the COB pool is a separate continuation rider that does not touch
//   the face. This matches the legacy `calcHybridPolicyTotalValue` two-pool
//   split (`policyMaximumBenefitAmount` + `policyContinuationBenefitAmount`).
//
// INFLATION RATES — the LTC pools inflate, the death benefit does NOT:
// - Base pool / monthly cap: `policyInflationProtection`.
// - COB pool: `policyInflationProtectionOnCOBAmount ?? policyInflationProtection`.
// - All capped at `policyInflationProtectionYears`; simple OR compound
//   (`isSimpleInflationProtection`).
//
// STATIC DEATH BENEFIT — BY DESIGN, a deliberate divergence from legacy
// (P2-16/17):
// - The life face is a FIXED amount. Even when the policy carries inflation
//   protection (which grows the LTC pools/cap), the death benefit is held flat
//   at the at-issue face (`dbRate` pinned at 0). The face-amount inflation
//   field (`policyInflationProtectionOnFaceAmount`) is deliberately NOT
//   honored for DB growth on generic hybrids; legacy inflated the DB via
//   `policyInflationProtectionOnFaceAmount ?? policyInflationProtection`
//   (legacy `calcFundingSourceDerivatives.ts` ~L2515-2522). Static-DB is the
//   declared model: it mirrors how CM II, Asset Care, and the other hybrid
//   strategies treat the face — the inflation rider escalates the LTC benefit,
//   not the life face. `hybridLifeInsurance` / `jointHybridAssetBased` are
//   accordingly in `hasFlatDeathBenefit` (FundingPolicyTypeDef.ts). The DB
//   only ever moves DOWN, via acceleration (base-pool drawdown), floored at
//   the residual minimum.
//
// PAYOUT MODEL — reimbursement (default) OR cash indemnity:
// - Reimbursement: pay `min(eligibleCost, proRatedCap)`, base pool first then
//   COB, each at its own inflated daily cap (mirrors the legacy daily-cap
//   payout).
// - Indemnity (`isIndemnityPolicyPayment === true`): pay the full pro-rated
//   monthly cap as cash regardless of actual cost, drawn base-then-COB; excess
//   over eligible cost accrues as `indemnitySurplus` (drained on a later
//   shortfall) via `applyIndemnitySurplus` — the same helper CM II and
//   life-with-rider use.
//
// RESIDUAL DEATH BENEFIT: floored at `policyMinimumGuaranteedBenefitAmount`
// (follows lifeWithRiderStrategy's floor handling). EP: standard two-tier
// (home/facility) WP, days strictly out-of-pocket (no retroactive lump).
// Standard 2-of-6-ADL gate.
//
// STRATEGY STATE carries `indemnitySurplus`; deltas surface the per-row
// accrual/application for the UI.
// ---------------------------------------------------------------------------

export interface GenericHybridState {
  // Indemnity-surplus reserve: indemnity dollars paid above eligible cost,
  // carried forward and drained on a later shortfall month. Always 0 for a
  // reimbursement policy.
  indemnitySurplus: number;
  // Joint (jointHybridAssetBased) only: per-claimant waiting-period balances.
  // The two pools (base/COB) and the death benefit are SHARED — both spouses
  // accelerate the same single face — but each serves their own elimination
  // period. The universal MonthlyState.waitingPeriod is single-insured, so for
  // joint we persist both spouses' EP here and surface the active claimant's
  // onto the universal slot per row. Undefined on single-life.
  jointWaitingPeriod?: JointWaitingPeriod;
}

export interface GenericHybridDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

interface InflationConfig {
  // Base pool / monthly-cap rate (legacy `policyInflationProtection`).
  baseRate: number;
  // COB pool rate (legacy `policyInflationProtectionOnCOBAmount ?? wide`).
  eobRate: number;
  // Death-benefit rate — always 0. The generic-hybrid default holds the life
  // face static (like CM II and the other hybrids); inflation grows the LTC
  // pools, never the death benefit.
  dbRate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    baseRate: policyWide,
    eobRate: ltcPolicy.policyInflationProtectionOnCOBAmount ?? policyWide,
    // Death benefit is STATIC by design (P2-16/17) — held at the at-issue face
    // even when inflation protection is present. The face-amount inflation knob
    // (`policyInflationProtectionOnFaceAmount`) is deliberately NOT read here,
    // diverging from legacy which inflated the face via it (matches CM II /
    // the other hybrids, where the rider escalates the LTC benefit, not the
    // life face — see the STATIC DEATH BENEFIT section in the header).
    dbRate: 0,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// Close-of-month pool balances below this snap to exactly 0, so floating-point
// residue can never keep an exhausted pool alive (the anniversary-accrual
// gates are bare `> 0` — a revived residue would re-accrue a closed pool).
const POOL_RESIDUE_EPSILON = 1e-6;

function snapExhaustedPool(balance: number): number {
  return balance < POOL_RESIDUE_EPSILON ? 0 : balance;
}

function readAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
} {
  return {
    baseAtIssue: ltcPolicy.policyMaximumBenefitAmount ?? 0,
    eobAtIssue: ltcPolicy.policyContinuationBenefitAmount ?? 0,
  };
}

export const genericHybridStrategy: PolicyProjectionStrategy<
  GenericHybridState,
  GenericHybridDeltas
> = {
  initClaimState(client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<GenericHybridState> {
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { baseRate, eobRate, dbRate, capYears, isSimple } = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated (the
    // projection only spans claim months), so pre-inflate each pool to its
    // value at claim start. A claim starting in policy year 1 keeps every
    // factor at 1.0. Each pool uses its own rate; the DB rate is 0 (static), so
    // the opening DB equals the raw at-issue face. Joint pre-inflates to the
    // shared anchor (earliest claim start across both spouses).
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart =
      baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart =
      eobAtIssue * growthFactor(eobRate, capYears, isSimple, claimStartPolicyYear);
    const dbAtClaimStart =
      baseAtIssue * growthFactor(dbRate, capYears, isSimple, claimStartPolicyYear);

    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
        ltcPolicy.homeCareWaitingPeriodDays ??
        0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };

    // Joint (jointHybridAssetBased): the pools/DB are SHARED — both spouses
    // accelerate the same single face — so only the per-spouse waiting period is
    // seeded into strategyState. Any `...DaysRemaining` partial credit belongs
    // to the primary client's in-flight claim only; the partner seeds from the
    // full policy EP. Single-life keeps just the surplus reserve.
    const isJoint = jointAnchorPolicyYear != null;
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - utilizedToDate, 0),
        extensionOfBenefitsPool: eobAtClaimStart,
        // The residual DB opens at the current (static) face net of base-pool
        // utilization, floored — a partially used policy opens reduced.
        deathBenefit: Math.max(dbAtClaimStart - utilizedToDate, minGuaranteed),
      },
      waitingPeriod,
      strategyState: isJoint
        ? {
            indemnitySurplus: 0,
            jointWaitingPeriod: seedJointWaitingPeriod(
              waitingPeriod,
              partnerWaitingPeriodSeed(ltcPolicy),
            ),
          }
        : { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy, jointAnchorPolicyYear): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization deducted from the base (death-benefit) pool only — the
    // COB pool is seeded at full and is NOT reduced by utilization (mirrors
    // `initClaimState`). Capped at the pre-inflated base pool at claim start,
    // inflated to the SAME anchor `initClaimState` used (the shared joint
    // anchor when the partner claims first) so the cap matches the deduction.
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(clientAsClient, 0);
    const baseAtClaimStart =
      baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const priorUtilization = Math.min(ltcPolicy.policyBenefitUtilizedToDate ?? 0, baseAtClaimStart);
    return {
      strategyId: 'generic-hybrid',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish); the helper honors explicit 0 and falls back to the
      // max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // The base / policy-wide rate drives the monthly cap. Surface the COB
      // rate on the EOB-pool knobs so the UI renders the continuation pool.
      inflationRate: baseRate,
      inflationCapYears: capYears,
      inflationIsSimple: isSimple,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { baseRate, eobRate, dbRate, capYears, isSimple } = readInflation(ltcPolicy);

    const baseFactor = growthFactor(baseRate, capYears, isSimple, policyYear);
    const eobFactor = growthFactor(eobRate, capYears, isSimple, policyYear);
    const dbFactor = growthFactor(dbRate, capYears, isSimple, policyYear);

    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    const inflatedEobMonthly = baseMonthly * eobFactor;
    const inflatedBase = baseAtIssue * baseFactor;
    const inflatedEob = eobAtIssue * eobFactor;
    // Death benefit is static by default (`dbRate === 0` → `dbFactor === 1`), so
    // the face stays at the at-issue amount regardless of policy year.
    const inflatedFace = baseAtIssue * dbFactor;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: inflatedEobMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: inflatedFace,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedBase + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      continuationMonthlyBenefitCap: inflatedEobMonthly,
      theoreticalLtcPoolCeiling: inflatedBase + inflatedEob,
    };
  },

  computeMonth(
    input: MonthComputeInput<GenericHybridState, GenericHybridDeltas>,
  ): MonthlyEntry<GenericHybridState, GenericHybridDeltas> {
    const {
      client,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
    } = input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Joint-ness is read from the carried state (per-claimant WP present), not
    // the row tag, so a joint run preserves both spouses' balances regardless.
    // The pools/DB are SHARED — both spouses accelerate the same single face.
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const isJoint = jointWaitingPeriodBefore != null;
    const activeClaimant: Claimant = claimant ?? 'client';

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification. Joint rides the
    // shared anchor clock (both spouses on one monotonic policy-year clock).
    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = isJoint
      ? resolveRowPolicyYear(clientAsClient, monthIndex, input.jointAnchorPolicyYear)
      : getCalendarPolicyYear(clientAsClient, monthIndex);

    // Each spouse serves their own elimination period: joint reads the active
    // claimant's balance from strategyState (the universal waitingPeriod slot is
    // rewritten per row and would otherwise carry the prior row's spouse).
    const activeWaitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;

    // The row's openState surfaces the active claimant's WP-before so identity
    // checks + UI read per-claimant. Shared pools pass through unchanged.
    const rowOpenState: MonthlyState<GenericHybridState> = isJoint
      ? { ...openState, waitingPeriod: activeWaitingPeriodBefore }
      : openState;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate — a care month before the premium-start year pays $0,
    // full OOP. Single-life floors its policy year at 1, so this is defensive;
    // a joint partner row can start before the premium year and must pay $0.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<GenericHybridState, GenericHybridDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible). Joint consumes the
    //    active claimant's per-spouse balance.
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      activeWaitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-pool monthly dollar caps for this policy year. Base cap grows on
    //    the base rate, COB cap on the COB rate — so once the base pool is
    //    exhausted and the draw moves to COB, the monthly limit reflects COB
    //    inflation.
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);
    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const baseMonthlyCap = baseMonthly * growthFactor(baseRate, capYears, isSimple, policyYear);
    const eobMonthlyCap = baseMonthly * growthFactor(eobRate, capYears, isSimple, policyYear);

    // 3. Annual anniversary accrual against the AT-ISSUE amount, gated on
    //    "pool still > 0" so an exhausted pool stays exhausted (an inflated
    //    ceiling never reopens a closed pool).
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        growthFactor(baseRate, capYears, isSimple, policyYear) -
        growthFactor(baseRate, capYears, isSimple, priorYear);
      const eobFactorDelta =
        growthFactor(eobRate, capYears, isSimple, policyYear) -
        growthFactor(eobRate, capYears, isSimple, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseAtIssue * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobAtIssue * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual = openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Payout: branches on indemnity vs. reimbursement. Both draw the base
    //    (death-benefit) pool first, then the COB pool.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromBase = 0;
    let drawFromEob = 0;
    let indemnityPaid = 0;

    if (isIndemnity && eligibleDays > 0 && baseAfterAccrual + eobAfterAccrual > 0) {
      // Pay the full pro-rated monthly cap as cash regardless of actual cost,
      // drawn base-then-COB. The cap follows whichever pool funds the day: base
      // days at the base cap, the spillover days at the COB cap. A pool's last
      // paying month may pay less than its cap when the pool can't cover it.
      const baseProRatedCap = baseMonthlyCap * (eligibleDays / DAYS_IN_MONTH);
      const baseDailyCap = baseMonthlyCap / DAYS_IN_MONTH;
      const eobDailyCap = eobMonthlyCap / DAYS_IN_MONTH;

      const baseDaysFunded =
        baseDailyCap > 0 ? Math.min(eligibleDays, baseAfterAccrual / baseDailyCap) : 0;
      drawFromBase = Math.min(baseDailyCap * baseDaysFunded, baseProRatedCap, baseAfterAccrual);

      const remainingDays = eligibleDays - baseDaysFunded;
      const eobDaysFunded =
        eobDailyCap > 0 ? Math.min(remainingDays, eobAfterAccrual / eobDailyCap) : 0;
      drawFromEob = Math.min(eobDailyCap * eobDaysFunded, eobAfterAccrual);

      indemnityPaid = drawFromBase + drawFromEob;
    } else {
      // Reimbursement, OR an indemnity row with no funds/eligible days to pay
      // (pools exhausted or WP-only). Reimburse each eligible day up to that
      // pool's daily cap, base first then COB. The base pool covers as many
      // eligible days as it allows (at the base cap); the remaining eligible
      // days fall to COB (at the COB cap). On an exhausted indemnity row this
      // pays $0, but the surplus drain below still runs against any shortfall.
      const dailyCost = costMetrics.dailyCareCost;
      const baseDailyCap = baseMonthlyCap / DAYS_IN_MONTH;
      const eobDailyCap = eobMonthlyCap / DAYS_IN_MONTH;

      const basePerDay = Math.min(dailyCost, baseDailyCap);
      const baseDaysFunded =
        basePerDay > 0 ? Math.min(eligibleDays, baseAfterAccrual / basePerDay) : 0;
      drawFromBase = Math.min(basePerDay * baseDaysFunded, baseAfterAccrual);

      const eobPerDay = Math.min(dailyCost, eobDailyCap);
      const eobDaysFunded =
        eobPerDay > 0 ? Math.min(eligibleDays - baseDaysFunded, eobAfterAccrual / eobPerDay) : 0;
      drawFromEob = Math.min(eobPerDay * eobDaysFunded, eobAfterAccrual);
    }

    // Gross cash this row pays out across both pools. The pools deplete by this
    // gross cash; the surplus model is a separate accounting reserve on top.
    const rowPayout = drawFromBase + drawFromEob;

    // 4b. Indemnity-surplus accounting (per-row), called UNCONDITIONALLY like
    //     CM II. Cost-coverage is capped at eligible cost; cash above eligible
    //     cost banks as surplus and drains on later shortfall rows WHILE the
    //     policy is still live. Once both pools are spent the reserve FREEZES (it
    //     must not bleed into post-exhaustion out-of-pocket cost). On
    //     reimbursement-election policies the open surplus is always 0 and the
    //     reimbursed payout never exceeds eligible cost, so this is a numeric
    //     no-op. WP-only / ineligible rows (eligibleCost 0) correctly never drain.
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Two pools (base + extension-of-benefits): exhausted only when BOTH spent.
      policyExhausted: baseAfterAccrual + eobAfterAccrual <= 0,
    });
    const ltcCoveragePaid = surplusResult.ltcCoveragePaid;

    // 5. Closing balances. Only base-pool drawdown accelerates (reduces) the
    //    death benefit; the COB pool is separate. DB floored at the residual.
    const closeBase = snapExhaustedPool(baseAfterAccrual - drawFromBase);
    const closeEob = snapExhaustedPool(eobAfterAccrual - drawFromEob);
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(openState.pools.deathBenefit - drawFromBase, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    // The row's applied cap follows the pool funding it: any base-pool dollars
    // this row reports the base cap (the transition row that exhausts the base
    // pool is still base-funded); only a row funded purely from the COB pool —
    // or a $0 row sitting fully in the COB phase — reports the COB-rate cap.
    const inCobPhase = drawFromBase <= 0 && (drawFromEob > 0 || (closeBase <= 0 && closeEob > 0));

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inCobPhase ? eobMonthlyCap : baseMonthlyCap,
      appliedCapPoolSource: inCobPhase ? 'eob' : 'base',
    };

    const ineligibleCost = costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost;

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<GenericHybridDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };

    // Joint: update only the active claimant's per-spouse EP in strategyState;
    // the universal waitingPeriod slot surfaces the same active-claimant value.
    // Pools/DB and the surplus reserve are shared. Single-life carries just the
    // surplus reserve.
    const closeStrategyState: GenericHybridState = jointWaitingPeriodBefore
      ? {
          indemnitySurplus: surplusResult.closeSurplus,
          jointWaitingPeriod: {
            ...jointWaitingPeriodBefore,
            [activeClaimant]: activeWaitingPeriodAfter,
          },
        }
      : { indemnitySurplus: surplusResult.closeSurplus };

    const closeState: MonthlyState<GenericHybridState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      strategyState: closeStrategyState,
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    // Transition milestones compare against `priorClaimantMonth` — the prior row
    // of THIS spouse — not the global previous row, which on a joint policy is
    // often the other spouse (the merged stream interleaves them). Single-life
    // priorClaimantMonth === priorMonth.
    const milestones = deriveStandardMilestones<GenericHybridState, GenericHybridDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: isJoint ? priorClaimantMonth : priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<GenericHybridState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<GenericHybridState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // The residual death benefit after acceleration, already floored each month.
  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<GenericHybridState>): number {
    return closingState.pools.deathBenefit;
  },
};
